import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Receipt, Eye, ShoppingBag } from 'lucide-react';
import SalesReceiptViewer from '@/components/SalesReceiptViewer';
import { formatCurrency } from '@/utils/currency';
import { SearchFilter } from '@/components/SearchFilter';

export default function SalesHistory() {
  const { token } = useContext(AuthContext);
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingSale, setViewingSale] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const response = await axios.get(`${API}/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data);
      setFilteredSales(response.data);
    } catch (error) {
      toast.error('Failed to load sales history');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      cash: 'bg-green-100 text-green-700',
      card: 'bg-blue-100 text-blue-700',
      bank_transfer: 'bg-purple-100 text-purple-700'
    };
    return colors[method] || 'bg-slate-100 text-slate-700';
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="sales-history-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sales History</h1>
            <p className="text-slate-600 mt-1">{sales.length} transactions completed</p>
          </div>
        </div>

        <SearchFilter
          data={sales}
          onFilter={setFilteredSales}
          searchFields={['sale_number', 'customer_name']}
          placeholder="Search by sale #, customer name..."
        />

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading sales...</p>
          </div>
        ) : sales.length === 0 ? (
          <Card className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Sales Yet</h3>
            <p className="text-slate-600">Sales transactions will appear here</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredSales.map((sale) => (
              <Card key={sale.id} className="p-6 hover:shadow-lg transition-all" data-testid={`sale-${sale.sale_number}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Receipt className="w-5 h-5 text-slate-600" />
                      <h3 className="text-xl font-bold text-slate-900">{sale.sale_number}</h3>
                      <Badge className={getPaymentMethodColor(sale.payment_method)}>
                        {sale.payment_method.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-slate-700 font-medium">
                      {sale.customer_name || 'Walk-in Customer'}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {new Date(sale.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-500">
                      Cashier: {sale.created_by}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-emerald-600 mb-3">
                      {formatCurrency(sale.total)}
                    </p>
                    <Button
                      onClick={() => {
                        setViewingSale(sale);
                        setShowViewer(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`view-receipt-${sale.sale_number}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Receipt
                    </Button>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Items ({sale.items.length}):
                  </p>
                  <div className="space-y-1">
                    {sale.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-slate-600">
                        <span>
                          {item.product_name} (x{item.quantity})
                          {item.discount > 0 && (
                            <span className="text-red-600 ml-2">-{formatCurrency(item.discount)}</span>
                          )}
                        </span>
                        <span className="font-medium">{formatCurrency(item.total)}</span>
                      </div>
                    ))}
                  </div>
                  {sale.notes && (
                    <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">
                      <span className="font-medium">Note:</span> {sale.notes}
                    </div>
                  )}
                  {sale.transfer_slip && sale.payment_method === 'bank_transfer' && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-slate-700 mb-2">Transfer Slip:</p>
                      <img 
                        src={sale.transfer_slip} 
                        alt="Transfer Slip" 
                        className="w-full h-48 object-contain border border-slate-200 rounded cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => window.open(sale.transfer_slip, '_blank')}
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Sales Receipt Viewer */}
        <SalesReceiptViewer
          sale={viewingSale}
          open={showViewer}
          onClose={() => {
            setShowViewer(false);
            setViewingSale(null);
          }}
        />
      </div>
    </Layout>
  );
}
